/**
 * product-form.js -- v19
 *
 * ROOT CAUSE FIX (confirmed by inspecting the actual network payload):
 * =====================================================================
 * The Shopify Subscriptions app block injects TWO selling_plan fields
 * into the form:
 *   1. selling_plan = "8851137101981"  (the actual plan ID, set by the widget)
 *   2. selling_plan = ""              (an empty trailing input, also from the app block)
 *
 * When FormData is built, both values are included. Shopify reads the LAST
 * selling_plan value — the empty string — and returns "already sold out"
 * because the product requires a valid selling plan.
 *
 * Fix: after building FormData, collect all selling_plan values with getAll(),
 * delete them all, then re-append only the first non-empty one (if any).
 * This ensures exactly one correct selling_plan value is sent.
 *
 * Architecture:
 *   - Our theme provides: input[name="id"], input[name="quantity"], change event on variant switch
 *   - Shopify Subscriptions app block provides: selling plan UI + selling_plan input(s)
 *   - We do NOT add our own selling_plan input
 */

document.addEventListener('DOMContentLoaded', function () {
  initProductForms();
  initProductCardButtons();
});

/* =========================================================
   PRODUCT PAGE FORM
   ========================================================= */
function initProductForms() {
  document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(function (form) {
    initVariantButtons(form);
    initAddToCart(form);
  });
}

function initVariantButtons(form) {
  var variantBtns = form.querySelectorAll('.product-page__variant-btn');
  var submitBtn = form.querySelector('.product-form__submit');

  // Set initial button state
  if (!variantBtns.length) {
    var variantInput = form.querySelector('[name="id"]');
    if (variantInput && submitBtn) {
      setSubmitBtnState(submitBtn, variantInput.dataset.available);
    }
    return;
  }

  variantBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      variantBtns.forEach(function (b) { b.classList.remove('active'); });
      btn.classList.add('active');

      var variantInput = form.querySelector('[name="id"]');
      if (variantInput) {
        variantInput.value = btn.dataset.variantId;
        variantInput.dataset.available = btn.dataset.available;
      }

      if (submitBtn) setSubmitBtnState(submitBtn, btn.dataset.available);

      // Dispatch change event so the Shopify Subscriptions app block
      // updates the selling plans for the newly selected variant.
      form.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  var activeBtn = form.querySelector('.product-page__variant-btn.active');
  if (activeBtn && submitBtn) setSubmitBtnState(submitBtn, activeBtn.dataset.available);
}

function setSubmitBtnState(submitBtn, available) {
  var btnText = submitBtn.querySelector('.btn-text');
  if (available === 'true' || available === true) {
    submitBtn.disabled = false;
    submitBtn.removeAttribute('disabled');
    if (btnText) btnText.textContent = 'Add to Cart';
  } else {
    submitBtn.disabled = true;
    submitBtn.setAttribute('disabled', 'disabled');
    if (btnText) btnText.textContent = 'Sold Out';
  }
}

function initAddToCart(form) {
  form.addEventListener('submit', function (e) {
    e.preventDefault();

    var submitBtn = form.querySelector('.product-form__submit');
    var btnText = submitBtn ? (submitBtn.querySelector('.purchase-selector__atc-label') || submitBtn.querySelector('.btn-text')) : null;
    var originalText = btnText ? btnText.textContent : 'Add to Cart';
    var errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    var errorMsg = form.querySelector('.product-form__error-message');

    if (submitBtn) {
      submitBtn.disabled = true;
      if (btnText) btnText.textContent = 'Adding...';
    }
    if (errorWrapper) errorWrapper.hidden = true;

    // Build FormData from the form.
    var formData = new FormData(form);

    // CRITICAL FIX: The Shopify Subscriptions app block injects TWO selling_plan
    // fields — one with the real plan ID and one empty string. Shopify reads the
    // last one (empty), causing "already sold out". We deduplicate: keep only the
    // first non-empty selling_plan value.
    var sellingPlanValues = formData.getAll('selling_plan');
    var validSellingPlan = sellingPlanValues.filter(function (v) {
      return v && v.trim() !== '';
    });
    formData.delete('selling_plan');
    if (validSellingPlan.length > 0) {
      formData.append('selling_plan', validSellingPlan[0]);
    }
    // If no valid selling_plan, we don't append one at all — one-time purchase.

    var variantId = parseInt(formData.get('id'), 10);
    if (!variantId || isNaN(variantId) || variantId <= 0) {
      if (errorWrapper) errorWrapper.hidden = false;
      if (errorMsg) errorMsg.textContent = 'Please select a variant.';
      if (submitBtn) { submitBtn.disabled = false; if (btnText) btnText.textContent = originalText; }
      return;
    }

    // POST to /cart/add with Accept: application/javascript.
    // This is exactly what Dawn does. The Accept header tells Shopify
    // to return JSON instead of an HTML redirect.
    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
        // No Content-Type — browser sets it automatically for FormData (multipart/form-data)
      },
      body: formData
    })
    .then(function (res) { return res.text(); })
    .then(function (text) {
      var data;
      try { data = JSON.parse(text); } catch (e) {
        throw new Error('Could not add item to cart. Please try again.');
      }
      if (data && data.status && data.status >= 400) {
        throw new Error(data.description || data.message || 'Could not add item to cart.');
      }
      if (submitBtn) { submitBtn.disabled = false; if (btnText) btnText.textContent = originalText; }
      openCartDrawer();
    })
    .catch(function (err) {
      if (errorWrapper) errorWrapper.hidden = false;
      if (errorMsg) errorMsg.textContent = err.message || 'An error occurred. Please try again.';
      if (submitBtn) { submitBtn.disabled = false; if (btnText) btnText.textContent = originalText; }
    });
  });
}

/* =========================================================
   PRODUCT CARD BUTTONS (Collection page)
   ========================================================= */
function initProductCardButtons() {
  // Variant button clicks — update purchase button variant IDs
  document.addEventListener('click', function (e) {
    var variantBtn = e.target.closest('.product-card__variant-btn');
    if (!variantBtn) return;
    var card = variantBtn.closest('.product-card');
    if (!card) return;
    card.querySelectorAll('.product-card__variant-btn').forEach(function (b) { b.classList.remove('active'); });
    variantBtn.classList.add('active');
    var newVariantId = variantBtn.dataset.variantId;
    var newPrice = variantBtn.dataset.price;
    card.querySelectorAll('.product-card__onetime-btn, .product-card__subscribe-btn').forEach(function (btn) {
      btn.dataset.variantId = newVariantId;
      var priceSpan = btn.querySelector('.product-card__btn-price');
      if (priceSpan && newPrice) priceSpan.textContent = newPrice;
    });
  });

  // Add-to-cart button clicks
  document.addEventListener('click', function (e) {
    var onetimeBtn = e.target.closest('.product-card__onetime-btn');
    var subscribeBtn = e.target.closest('.product-card__subscribe-btn');
    var btn = onetimeBtn || subscribeBtn;
    if (!btn) return;

    e.preventDefault();
    var variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId || isNaN(variantId)) return;

    var labelEl = btn.querySelector('.product-card__btn-label');
    var originalLabel = labelEl ? labelEl.textContent : '';
    btn.disabled = true;
    btn.style.opacity = '0.7';
    if (labelEl) labelEl.textContent = 'Adding...';

    var lineItem = { id: variantId, quantity: 1 };

    // For subscribe button: include selling_plan if available
    if (subscribeBtn) {
      var sellingPlan = btn.dataset.sellingPlan;
      if (sellingPlan && sellingPlan !== '') {
        var planId = parseInt(sellingPlan, 10);
        if (!isNaN(planId) && planId > 0) {
          lineItem.selling_plan = planId;
        }
      }
    }
    // One-time button: never include selling_plan

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [lineItem] })
    })
    .then(function (res) { return res.text(); })
    .then(function (text) {
      var data;
      try { data = JSON.parse(text); } catch (e) {
        throw new Error('Could not add item to cart. Please try again.');
      }
      if (data && data.status && data.status >= 400) {
        throw new Error(data.description || data.message || 'Could not add item to cart.');
      }
      btn.disabled = false; btn.style.opacity = '';
      if (labelEl) labelEl.textContent = originalLabel;
      openCartDrawer();
    })
    .catch(function (err) {
      btn.disabled = false; btn.style.opacity = '';
      if (labelEl) labelEl.textContent = originalLabel;
      var card = btn.closest('.product-card');
      var errEl = card ? card.querySelector('.product-card__error') : null;
      if (errEl) {
        errEl.textContent = err.message || 'Could not add to cart. Please try again.';
        errEl.hidden = false;
        setTimeout(function () { errEl.hidden = true; }, 4000);
      } else {
        alert(err.message || 'Could not add to cart. Please try again.');
      }
    });
  });
}

/* =========================================================
   CART DRAWER OPENER
   ========================================================= */
function openCartDrawer() {
  if (window.cartDrawer && typeof window.cartDrawer.open === 'function') {
    window.cartDrawer.open();
  } else {
    document.dispatchEvent(new CustomEvent('cart:open'));
  }
}
